import { useCallback, useEffect, useState } from 'react';
import { apiGet } from '~/lib/api';

/** Anglers — customer records with lifetime value + trip counts. */

interface CustomerRow {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  notes: string | null;
  marketing_opt_in: boolean;
  lifetime_value_cents: number;
  trip_count: number;
  last_trip_at: string | null;
  created_at: string;
}

const money = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
    cents / 100,
  );

export function Anglers() {
  const [items, setItems] = useState<CustomerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ per_page: '100' });
    if (search) params.set('q', search);
    apiGet<{ items: CustomerRow[]; total: number }>(`/api/admin/customers?${params}`)
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
        setError(null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(load, [load]);

  return (
    <div className="trc-page">
      <header className="trc-page-header">
        <p className="trc-eyebrow">Operations</p>
        <h1 className="trc-osw trc-page-title">Anglers</h1>
      </header>

      <div className="trc-tabs">
        <form
          className="trc-tab-search"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(q.trim());
          }}
        >
          <input placeholder="Search name, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} />
        </form>
      </div>

      {loading && <p>Loading…</p>}
      {error && <p className="trc-error">Error: {error}</p>}
      {!loading && !error && items.length === 0 && (
        <p className="trc-page-empty">No anglers yet — customer records are created at checkout.</p>
      )}

      {items.length > 0 && (
        <div className="trc-table-wrap">
          <table className="trc-table">
            <thead>
              <tr>
                <th>Angler</th>
                <th>Contact</th>
                <th>Trips</th>
                <th>Lifetime value</th>
                <th>Last trip</th>
                <th>Marketing</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</strong>
                    {c.notes && <div className="trc-cell-sub">{c.notes}</div>}
                  </td>
                  <td>
                    {c.email ?? '—'}
                    {c.phone && <div className="trc-cell-sub">{c.phone}</div>}
                  </td>
                  <td>{c.trip_count}</td>
                  <td>{money(c.lifetime_value_cents)}</td>
                  <td>{c.last_trip_at ? c.last_trip_at.slice(0, 10) : '—'}</td>
                  <td>{c.marketing_opt_in ? 'opted in' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > items.length && (
        <p className="trc-page-empty">Showing first {items.length} of {total} — refine the search.</p>
      )}
    </div>
  );
}
